// Waiver Template Types

export interface WaiverSection {
  id: string;
  type: 'heading' | 'rich_text' | 'image' | 'divider' | 'bullet_list' | 'numbered_list';
  order: number;
  content: string; // HTML for rich_text, URL for image
  items: string[]; // For bullet/numbered lists
  headingLevel: number; // 1-6 for headings
}

export interface FieldRequirement {
  enabled: boolean;
  required: boolean;
}

export interface WaiverRequiredFields {
  fullName: boolean;
  address: FieldRequirement;
  phone: FieldRequirement;
  email: FieldRequirement;
  emergencyContact: FieldRequirement;
  idNumber: FieldRequirement;
  idIssueDate: FieldRequirement;
  dateOfBirth: FieldRequirement;
}

export interface SignatureSettings {
  graphicalSignature: boolean;
  checkboxAgreement: boolean;
  requireBoth: boolean;
  checkboxText?: string;
}

export const DEFAULT_CHECKBOX_TEXT = 'I have read and agree to all terms and conditions outlined in this waiver. I understand the risks involved and release the field from liability.';

export interface WaiverTemplate {
  id: string;
  fieldId: string;
  year: number;
  name: string;
  sections: WaiverSection[];
  requiredFields: WaiverRequiredFields;
  signatureSettings: SignatureSettings;
  parentalConsentAgeThreshold: number;
  isActive: boolean;
  signatureCount: number;
  isLocked: boolean;
  version: number;
  copiedFromTemplateId?: string;
  imageUrl?: string;
  createdAt: string;
  updatedAt: string;
}

// Signed Waiver Types

export interface GuestInfo {
  fullName: string;
  email?: string;
  phone?: string;
  searchableName: string;
}

export interface AddressInfo {
  street: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface EmergencyContactInfo {
  name: string;
  phone: string;
  relationship: string;
}

export interface WaiverFormData {
  fullName: string;
  dateOfBirth?: string;
  address?: AddressInfo;
  phone?: string;
  email?: string;
  emergencyContact?: EmergencyContactInfo;
  idNumber?: string;
  idIssueDate?: string;
}

export interface SignatureData {
  graphicalSignatureData?: string; // Base64 PNG
  checkboxAgreed: boolean;
  typedName?: string;
}

export interface SignedWaiver {
  id: string;
  fieldId: string;
  templateId: string;
  year: number;
  userId?: string;
  signerType: 'registered' | 'guest' | 'child';
  guestInfo?: GuestInfo;
  parentUserId?: string;
  childUserId?: string;
  isMinor: boolean;
  formData: WaiverFormData;
  signatureData: SignatureData;
  parentSignatureData?: SignatureData;
  signedAt: string;
  ipAddress?: string;
  userAgent?: string;
  pdfUrl?: string;
  pdfGeneratedAt?: string;
}

// Check-In Types

export interface CheckInPlayerInfo {
  name: string;
  fullName?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  isGuest: boolean;
}

export interface WaiverCheckStatus {
  hasValidWaiver: boolean;
  waiverId?: string;
  signedAt?: string;
  signerType?: string;
}

export interface CheckIn {
  id: string;
  fieldId: string;
  date: string;
  userId?: string;
  rfidTag?: string;
  playerInfo: CheckInPlayerInfo;
  waiverStatus: WaiverCheckStatus;
  gameType?: string;
  isConfirmed: boolean;
  confirmedAt?: string;
  confirmedByUserId?: string;
  notes?: string;
  scannedAt: string;
}

// Request Types

export interface CreateWaiverTemplateRequest {
  name: string;
  year: number;
  sections: Omit<WaiverSection, 'id'>[];
  requiredFields: WaiverRequiredFields;
  signatureSettings: SignatureSettings;
  parentalConsentAgeThreshold: number;
}

export interface UpdateWaiverTemplateRequest {
  name: string;
  sections: Omit<WaiverSection, 'id'>[];
  requiredFields: WaiverRequiredFields;
  signatureSettings: SignatureSettings;
  parentalConsentAgeThreshold: number;
}

export interface SignWaiverRequest {
  fieldId: string;
  formData: WaiverFormData;
  signatureData: SignatureData;
}

export interface GuestInfoRequest {
  fullName: string;
  email?: string;
  phone?: string;
}

export interface SignWaiverAsGuestRequest {
  fieldId: string;
  guestInfo: GuestInfoRequest;
  formData: WaiverFormData;
  signatureData: SignatureData;
}

export interface SignWaiverForChildRequest {
  fieldId: string;
  childUserId?: string;
  childFormData: WaiverFormData;
  parentSignatureData: SignatureData;
}

export interface ProcessRfidScanRequest {
  rfidTag: string;
}

export interface ConfirmCheckInRequest {
  gameType: string;
}

export interface CreateCheckInForUserRequest {
  userId: string;
}

export interface CreateCheckInForGuestRequest {
  signedWaiverId: string;
}

// Response Types

export interface WaiverStatusResponse {
  hasValidWaiver: boolean;
  signedAt?: string;
  waiverId?: string;
  year?: number;
}

export interface CheckInResponse {
  checkInId: string;
  playerInfo: CheckInPlayerInfo;
  waiverStatus: WaiverCheckStatus;
  isConfirmed: boolean;
  scannedAt: string;
}

export interface TodayCheckInsResponse {
  checkIns: CheckIn[];
  totalCount: number;
  confirmedCount: number;
}

export interface CheckInHistoryResponse {
  checkIns: CheckIn[];
  totalCount: number;
  gameTypeStats: Record<string, number>;
}

export interface PlayerSearchResult {
  userId?: string;
  signedWaiverId?: string;
  fullName?: string;
  callsign?: string;
  email?: string;
  phone?: string;
  dateOfBirth?: string;
  isGuest: boolean;
  hasValidWaiver: boolean;
  lastVisit?: string;
}

export interface CanEditTemplateResponse {
  canEdit: boolean;
  signatureCount: number;
  isLocked: boolean;
}
